import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API_URL, getApiCandidates } from "../apiConfig";
import { persistAuthTokens } from "./session";
import {
  clearDeliveryCourierSession,
  getAppType,
  isDeliveryCourierApp,
  isDeliveryCourierPath,
  isDriverYalaUI,
  isDeliveryUberUI,
  isRiderLyftUI,
  markDeliveryCourierSession,
} from "../native/platform";
import "../delivery/delivery-uber.css";

const logoSrc = "/yala-logo.png";
const riderLogoSrc = "/yala-rider-logo.png";
const driverLogoSrc = "/yala-driver-logo.png";
const deliveryLogoSrc = "/yala-delivery-logo.png";

function getLogoForApp() {
  const appType = getAppType();
  if (appType === "rider") return riderLogoSrc;
  if (appType === "driver") return driverLogoSrc;
  if (appType === "delivery" || isDeliveryCourierApp()) return deliveryLogoSrc;
  return logoSrc;
}

/**
 * Compresses an image file to fit within maxSizeKB (default 600KB).
 * Returns a new File object with reduced dimensions and quality.
 * Non-image files (PDFs) are returned unchanged.
 */
function compressImage(file, maxSizeKB = 600, maxDimension = 1024) {
  return new Promise((resolve) => {
    if (!file || !file.type.startsWith("image/")) {
      resolve(file);
      return;
    }
    // If already small enough, skip compression
    if (file.size <= maxSizeKB * 1024) {
      resolve(file);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;

      // Scale down if exceeds max dimension
      if (width > maxDimension || height > maxDimension) {
        const ratio = Math.min(maxDimension / width, maxDimension / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      // Try progressively lower quality until under maxSizeKB
      let quality = 0.7;
      const outputType = "image/jpeg";

      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            if (blob.size > maxSizeKB * 1024 && quality > 0.3) {
              quality -= 0.1;
              tryCompress();
            } else {
              const compressedFile = new File(
                [blob],
                file.name.replace(/\.[^.]+$/, ".jpg"),
                { type: outputType }
              );
              resolve(compressedFile);
            }
          },
          outputType,
          quality
        );
      };
      tryCompress();
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // Fall back to original if compression fails
    };
    img.src = url;
  });
}

export function getRegistrationAppContext() {
  const appType = getAppType();
  // Native / stamped app type always wins — never inherit courier UI in Rider/Driver.
  if (appType === "rider") return "rider";
  if (appType === "driver") return "driver";
  if (appType === "delivery") return "delivery";
  if (isDeliveryCourierApp() || isDeliveryUberUI()) return "delivery";
  return "web";
}

export function getLockedRegistrationUserType() {
  const context = getRegistrationAppContext();
  if (context === "rider") return "rider";
  if (context === "driver" || context === "delivery") return "driver";
  return getInitialUserType();
}

/**
 * Determines the initial user type for the registration form.
 * Priority: getAppType() (if "driver" or "rider") > URL ?role= param > default "rider"
 * When getAppType() returns a known app type, it is final and non-overridable.
 */
const getRequestedRole = () => {
  const params = new URLSearchParams(window.location.search);
  const requestedRole = params.get("role");
  const nextParam = params.get("next") || "";
  const storedNext = localStorage.getItem("sx_login_redirect") || "";

  const normalizeRoute = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    try {
      if (raw.startsWith("http://") || raw.startsWith("https://")) {
        return new URL(raw).pathname || "";
      }
      return raw.startsWith("/") ? raw : `/${raw.replace(/^\/+/, "")}`;
    } catch (error) {
      return raw.startsWith("/") ? raw : `/${raw.replace(/^\/+/, "")}`;
    }
  };

  const nextRoute = normalizeRoute(nextParam || storedNext);
  if (nextRoute === "/admin" || nextRoute === "/admin-dashboard" || nextRoute.startsWith("/admin/")) {
    return "admin";
  }
  const appType = getAppType();
  if (appType !== "rider" && appType !== "driver" && isDeliveryCourierPath(nextRoute)) {
    return "driver";
  }

  return requestedRole === "driver" || requestedRole === "rider" || requestedRole === "admin"
    ? requestedRole
    : "";
};

const getInitialUserType = () => {
  const appType = getAppType();
  if (appType === "rider") {
    return "rider";
  }
  if (appType === "driver") {
    return "driver";
  }
  if (appType === "delivery" || isDeliveryCourierApp()) {
    return "driver";
  }
  const requestedRole = getRequestedRole();
  if (requestedRole === "admin") return "admin";
  return requestedRole === "driver" ? "driver" : "rider";
};

/**
 * Returns true when the app type is known (driver or rider),
 * meaning the user type toggle should be hidden and user_type is locked.
 */
const isAppTypeLocked = () => {
  const appType = getAppType();
  const requestedRole = getRequestedRole();
  return appType === "driver" || appType === "rider" || appType === "delivery" || isDeliveryCourierApp() || Boolean(requestedRole);
};

function Register() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    gender: "Male",
    phone_number: "",
    national_id_number: "",
    city: "",
    password: "",
    user_type: getLockedRegistrationUserType(),
  });
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [nationalIdDocument, setNationalIdDocument] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationStep, setVerificationStep] = useState(false);
  const [debugCode, setDebugCode] = useState("");
  const registrationRoles = !verificationStep && !isAppTypeLocked()
    ? ["rider", "driver"]
    : [];

  useEffect(() => {
    const appType = getAppType();
    if (appType === "rider" || appType === "driver") {
      clearDeliveryCourierSession();
      localStorage.removeItem("sx_login_redirect");
    }

    const lockedType = getLockedRegistrationUserType();
    setFormData((current) =>
      current.user_type === lockedType ? current : { ...current, user_type: lockedType },
    );
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadCities = async () => {
      let lastError = null;

      for (const endpoint of getApiCandidates("/cities/")) {
        try {
          const { data } = await axios.get(endpoint);
          if (isActive) {
            const cityGroups = Array.isArray(data) ? data : [];
            setCities(
              cityGroups.flatMap((region) =>
                (region.cities || []).map((city) => ({
                  ...city,
                  region_name: region.name,
                })),
              ),
            );
            setErrorMessage("");
          }
          return;
        } catch (error) {
          lastError = error;
          if (error?.response) {
            break;
          }
        }
      }

      if (isActive) {
        const deliveryFlow = isDeliveryCourierApp() || isDeliveryUberUI() || getAppType() === "delivery";
        setErrorMessage(
          lastError?.response
            ? "Unable to load cities. Run: python manage.py bootstrap_yala"
            : deliveryFlow
              ? "Cannot reach the Yala Delivery server. Connect your phone to the same Wi‑Fi as this PC, keep the backend running, then retry."
              : "Unable to reach the Yala server. Start backend on port 8000.",
        );
      }
    };

    loadCities()
      .finally(() => {
        if (isActive) {
          setCitiesLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  const handleChange = (event) => {
    // Prevent overriding user_type when app type is locked
    if (event.target.name === "user_type" && isAppTypeLocked()) {
      return;
    }
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
    // Clear field-specific error when user edits that field
    if (fieldErrors[event.target.name]) {
      setFieldErrors((prev) => {
        const updated = { ...prev };
        delete updated[event.target.name];
        return updated;
      });
    }
  };

  const registerUser = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setFieldErrors({});

    if (!formData.city) {
      setErrorMessage("Please select your city.");
      return;
    }

    if (formData.user_type === "rider" && !profilePicture) {
      setErrorMessage(t("auth.riderPhotoRequired"));
      return;
    }

    if (formData.user_type === "rider" && !nationalIdDocument) {
      setErrorMessage(t("auth.nationalIdDocumentRequired"));
      return;
    }

    // Validate file types before submission
    const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
    const ALLOWED_DOC_TYPES = [...ALLOWED_IMAGE_TYPES, "application/pdf"];

    if (profilePicture && !ALLOWED_IMAGE_TYPES.includes(profilePicture.type)) {
      setErrorMessage("Profile photo must be a JPG, PNG, or WebP image.");
      return;
    }

    if (nationalIdDocument && !ALLOWED_DOC_TYPES.includes(nationalIdDocument.type)) {
      setErrorMessage("National ID must be uploaded as a PDF, JPG, PNG, or WebP file. Other formats like AVIF or AVI are not supported.");
      return;
    }

    try {
      setLoading(true);

      const lockedUserType = getLockedRegistrationUserType();

      const postWithFallback = async (path, data, config = {}) => {
        let lastError = null;
        const headers = { ...(config.headers || {}) };
        // Ensure Content-Type for JSON bodies on native (Capacitor HTTP doesn't auto-detect)
        if (data && !(data instanceof FormData) && !headers["Content-Type"] && !headers["content-type"]) {
          headers["Content-Type"] = "application/json";
        }
        const mergedConfig = { ...config, headers };
        for (const endpoint of getApiCandidates(path)) {
          try {
            return await axios.post(endpoint, data, mergedConfig);
          } catch (error) {
            lastError = error;
            if (error?.response) {
              throw error;
            }
          }
        }
        throw lastError || new Error("Network request failed");
      };

      // --- Registration request (multipart with files) ---
      // Compress images before upload to avoid nginx 413 errors
      const compressedProfilePic = profilePicture ? await compressImage(profilePicture, 600, 1024) : null;
      const compressedNationalId = nationalIdDocument ? await compressImage(nationalIdDocument, 600, 1024) : null;

      const payload = new FormData();
      Object.entries({ ...formData, user_type: lockedUserType }).forEach(([key, value]) => {
        payload.append(key, String(value ?? ""));
      });

      if (compressedProfilePic) {
        payload.append("profile_picture", compressedProfilePic);
      }

      if (compressedNationalId) {
        payload.append("national_id_document", compressedNationalId);
      }

      await postWithFallback("/auth/register/", payload, {
        headers: {
          "X-App-Type": getAppType() || lockedUserType,
        },
      });

      if (formData.user_type !== lockedUserType) {
        setFormData((current) => ({ ...current, user_type: lockedUserType }));
      }

      const loginResponse = await postWithFallback("/auth/login/", {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      });

      persistAuthTokens({
        access: loginResponse.data.access,
        refresh: loginResponse.data.refresh,
        user: loginResponse.data,
      });

      const params = new URLSearchParams(window.location.search);
      const nextRoute = params.get("next") || localStorage.getItem("sx_login_redirect") || "";
      if (
        formData.user_type === "driver" &&
        (isDeliveryCourierApp() || isDeliveryUberUI() || getAppType() === "delivery" || String(nextRoute).includes("/delivery/"))
      ) {
        markDeliveryCourierSession();
        localStorage.removeItem("needs_payment_setup");
        localStorage.removeItem("needs_vehicle_setup");
        window.location.replace("/delivery/profile-setup");
        return;
      }

      const codeResponse = await postWithFallback(
        "/auth/phone/request-code/",
        {},
        { headers: { Authorization: `Bearer ${loginResponse.data.access}` } },
      );
      setDebugCode(codeResponse.data.debug_code || "");
      setVerificationStep(true);
      return;
    } catch (error) {
      const status = error.response?.status;
      const response = error.response?.data || {};

      // Detect nginx HTML error pages (413 Request Entity Too Large, 502, etc.)
      const responseStr = typeof response === "string" ? response : "";
      if (status === 413 || responseStr.includes("413") || responseStr.includes("Request Entity Too Large")) {
        setErrorMessage("Files are too large. Please use smaller photos (under 2MB each).");
        setFieldErrors({});
      } else if (typeof response === "string" && response.includes("<html")) {
        // Server returned HTML error page instead of JSON
        setErrorMessage(`Server error (${status || "unknown"}). Please try again.`);
        setFieldErrors({});
      } else {
        // Build field-specific errors map from DRF response
        const knownFields = [
          "email", "first_name", "last_name", "gender", "national_id_number",
          "city", "password", "user_type", "phone_number", "profile_picture",
          "national_id_document", "app_type",
        ];
        const newFieldErrors = {};
        for (const field of knownFields) {
          const val = response[field];
          if (val) {
            newFieldErrors[field] = Array.isArray(val) ? val[0] : val;
          }
        }
        setFieldErrors(newFieldErrors);

        // Also set a top-level summary message
        const fieldErrorMessages = Object.values(newFieldErrors);
        if (fieldErrorMessages.length > 0) {
          setErrorMessage(fieldErrorMessages[0]);
        } else if (response.non_field_errors?.[0]) {
          setErrorMessage(response.non_field_errors[0]);
        } else if (response.detail) {
          setErrorMessage(response.detail);
        } else if (response.error) {
          setErrorMessage(response.error);
        } else if (error.response && typeof response === "object" && Object.keys(response).length > 0) {
          const firstKey = Object.keys(response)[0];
          const firstVal = response[firstKey];
          const errorText = Array.isArray(firstVal) ? firstVal[0] : firstVal;
          setErrorMessage(`${firstKey}: ${errorText}`);
        } else if (error.response) {
          // Empty response body — show status code and raw data for debugging
          const statusCode = error.response.status || "unknown";
          const rawData = error.response.data;
          const debugInfo = rawData ? (typeof rawData === "string" ? rawData.substring(0, 100) : JSON.stringify(rawData).substring(0, 100)) : "no body";
          setErrorMessage(`Error (${statusCode}): ${debugInfo}`);
        } else {
          setErrorMessage(`Network error: ${error.message}`);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const citiesByRegion = cities.reduce((groups, city) => {
    const regionName = city.region_name || "Other";
    if (!groups[regionName]) {
      groups[regionName] = [];
    }
    groups[regionName].push(city);
    return groups;
  }, {});

  const finishRegistration = () => {
      if (formData.user_type === "rider") {
        localStorage.setItem("needs_payment_setup", "true");
        localStorage.removeItem("needs_vehicle_setup");
        window.location.replace("/payment-setup");
        return;
      }

      if (formData.user_type === "driver") {
        localStorage.removeItem("needs_payment_setup");
        const params = new URLSearchParams(window.location.search);
        const nextRoute = params.get("next") || localStorage.getItem("sx_login_redirect") || "";
        if (
          isDeliveryCourierApp() ||
          isDeliveryUberUI() ||
          String(nextRoute).includes("/delivery/")
        ) {
          markDeliveryCourierSession();
          localStorage.removeItem("needs_vehicle_setup");
          window.location.replace("/delivery/profile-setup");
          return;
        }
        clearDeliveryCourierSession();
        localStorage.setItem("needs_vehicle_setup", "true");
        window.location.replace("/driver-vehicle-setup");
        return;
      }

      if (formData.user_type === "admin") {
        localStorage.removeItem("needs_payment_setup");
        localStorage.removeItem("needs_vehicle_setup");
        window.location.replace("/admin");
        return;
      }

      window.location.replace("/");
  };

  const verifyPhone = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    try {
      setLoading(true);
      await axios.post(
        `${API_URL}/auth/phone/verify/`,
        { code: verificationCode },
        { headers: { Authorization: `Bearer ${localStorage.getItem("access")}` } },
      );
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem("user", JSON.stringify({ ...user, phone_verified: true }));
      finishRegistration();
    } catch (error) {
      const response = error.response?.data || {};
      setErrorMessage(response.error || response.detail || "Phone verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const registrationContext = getRegistrationAppContext();
  const isDeliveryCourierAppFlow = registrationContext === "delivery";
  const isRiderLyft = registrationContext === "rider" || isRiderLyftUI();
  const isDriverLyft =
    registrationContext === "driver" ||
    (!isDeliveryCourierAppFlow && (isDriverYalaUI() || formData.user_type === "driver"));
  const isDeliveryUber = isDeliveryCourierAppFlow;

  const registerTitle = isDeliveryCourierAppFlow
    ? t("auth.courierRegisterTitle")
    : registrationContext === "rider"
      ? t("auth.riderRegisterTitle")
      : registrationContext === "driver"
        ? t("auth.driverRegisterTitle")
        : t("auth.registerTitle");
  const registerSubtitle = isDeliveryCourierAppFlow
    ? t("auth.courierRegisterSubtitle")
    : registrationContext === "rider"
      ? t("auth.riderRegisterSubtitle")
      : registrationContext === "driver"
        ? t("auth.driverRegisterSubtitle")
        : t("auth.registerSubtitle");
  const accountLabel = verificationStep
    ? "Phone verification"
    : registrationContext === "rider" || formData.user_type === "rider"
      ? t("auth.riderAccount")
      : formData.user_type === "admin"
        ? "Admin Account"
        : isDeliveryCourierAppFlow
          ? t("auth.courierAccount")
          : t("auth.driverAccount");

  const heroBrand = isDeliveryCourierAppFlow
    ? "Yala Delivery"
    : registrationContext === "rider"
      ? "Yala Rider"
      : registrationContext === "driver"
        ? "Yala Driver"
        : t("auth.join");

  return (
    <main className={`auth-register-page${isRiderLyft ? " auth-register-page--lyft" : ""}${isDriverLyft ? " auth-register-page--lyft-driver auth-register-page--lyft" : ""}${isDeliveryUber ? " auth-register-page--delivery yala-login--delivery-uber" : ""}`}>
      <AuthRegisterStyles />
      <section className="auth-register-hero">
        <img src={getLogoForApp()} alt={heroBrand} />
        <span>{heroBrand}</span>
        <h1>{registerTitle}</h1>
        <p>{registerSubtitle}</p>
      </section>

      <form
        className="auth-register-card"
        onSubmit={verificationStep ? verifyPhone : registerUser}
      >
        <div className="auth-register-header">
          <span>{accountLabel}</span>
          <h2>{verificationStep ? "Enter your SMS code" : t("auth.signUp")}</h2>
        </div>

        {!verificationStep && !isAppTypeLocked() && <div className="auth-register-tabs">
          {registrationRoles.map((type) => (
            <button
              key={type}
              type="button"
              className={formData.user_type === type ? "active" : ""}
              onClick={() => {
                setFormData({ ...formData, user_type: type });
                if (type !== "rider") {
                  setProfilePicture(null);
                  setNationalIdDocument(null);
                }
              }}
            >
              {type === "rider" ? t("common.rider") : t("common.driver")}
            </button>
          ))}
        </div>}

        {errorMessage && <div className="auth-register-error">{errorMessage}</div>}

        {verificationStep ? (
          <>
            <p className="auth-register-verification-copy">
              We sent a six-digit code to {formData.phone_number}. Verify it before
              Yala sends your application to admin review.
            </p>
            {debugCode && (
              <div className="auth-register-debug-code">
                Local test code: <strong>{debugCode}</strong>
              </div>
            )}
            <input
              value={verificationCode}
              onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              required
            />
          </>
        ) : <>
        <div className="auth-register-grid">
          <div>
            <input
              name="first_name"
              placeholder={t("auth.firstName")}
              value={formData.first_name}
              onChange={handleChange}
              autoComplete="given-name"
            />
            {fieldErrors.first_name && <span className="auth-register-field-error">{fieldErrors.first_name}</span>}
          </div>

          <div>
            <input
              name="last_name"
              placeholder={t("auth.lastName")}
              value={formData.last_name}
              onChange={handleChange}
              autoComplete="family-name"
            />
            {fieldErrors.last_name && <span className="auth-register-field-error">{fieldErrors.last_name}</span>}
          </div>
        </div>

        <input
          name="email"
          type="email"
          placeholder={t("auth.email")}
          value={formData.email}
          onChange={handleChange}
          autoComplete="email"
        />
        {fieldErrors.email && <span className="auth-register-field-error">{fieldErrors.email}</span>}

        <div className="auth-register-grid">
          <div>
            <select name="gender" value={formData.gender} onChange={handleChange}>
              <option value="Male">{t("auth.male")}</option>
              <option value="Female">{t("auth.female")}</option>
            </select>
            {fieldErrors.gender && <span className="auth-register-field-error">{fieldErrors.gender}</span>}
          </div>

          <div>
            <input
              name="phone_number"
              type="tel"
              placeholder={t("auth.phonePlaceholder")}
              value={formData.phone_number}
              onChange={handleChange}
              autoComplete="tel"
            />
            {fieldErrors.phone_number && <span className="auth-register-field-error">{fieldErrors.phone_number}</span>}
          </div>
        </div>

        <input
          name="national_id_number"
          placeholder={t("auth.nationalId")}
          value={formData.national_id_number}
          onChange={handleChange}
        />
        {fieldErrors.national_id_number && <span className="auth-register-field-error">{fieldErrors.national_id_number}</span>}

        <label className="auth-register-city">
          <strong>City</strong>
          <span>Select the city where you will use Yala.</span>
          <select
            name="city"
            value={formData.city}
            onChange={handleChange}
            required
            disabled={citiesLoading}
          >
            <option value="">
              {citiesLoading ? "Loading cities..." : "Select your city"}
            </option>
            {Object.entries(citiesByRegion).map(([regionName, regionCities]) => (
              <optgroup key={regionName} label={regionName}>
                {regionCities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {fieldErrors.city && <span className="auth-register-field-error">{fieldErrors.city}</span>}
        </label>

        {formData.user_type === "rider" && (
          <>
            <label className="auth-register-file">
              <strong>{t("auth.riderPhotoRequired")}</strong>
              <span>{profilePicture?.name || t("auth.riderPhotoHelp")}</span>
              <span style={{fontSize: "11px", color: "rgba(255,255,255,0.5)"}}>Accepted: JPG, PNG, WebP (max 5MB)</span>
              {profilePicture && !["image/jpeg", "image/png", "image/webp"].includes(profilePicture.type) && (
                <span style={{fontSize: "12px", color: "#f87171", fontWeight: 700}}>⚠ Invalid file type. Please choose a JPG, PNG, or WebP image.</span>
              )}
              {fieldErrors.profile_picture && <span className="auth-register-field-error">{fieldErrors.profile_picture}</span>}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                required
                onChange={(event) => setProfilePicture(event.target.files?.[0] || null)}
              />
            </label>

            <label className="auth-register-file">
              <strong>{t("auth.nationalIdDocumentRequired")}</strong>
              <span>{nationalIdDocument?.name || t("auth.nationalIdDocumentHelp")}</span>
              <span style={{fontSize: "11px", color: "rgba(255,255,255,0.5)"}}>Accepted: PDF, JPG, PNG, WebP (max 8MB)</span>
              {nationalIdDocument && !["image/jpeg", "image/png", "image/webp", "application/pdf"].includes(nationalIdDocument.type) && (
                <span style={{fontSize: "12px", color: "#f87171", fontWeight: 700}}>⚠ Invalid file type: {nationalIdDocument.type || "unknown"}. Please choose a PDF, JPG, PNG, or WebP file.</span>
              )}
              {fieldErrors.national_id_document && <span className="auth-register-field-error">{fieldErrors.national_id_document}</span>}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                required
                onChange={(event) => setNationalIdDocument(event.target.files?.[0] || null)}
              />
            </label>
          </>
        )}

        <input
          name="password"
          type="password"
          placeholder={t("auth.password")}
          value={formData.password}
          onChange={handleChange}
          autoComplete="new-password"
        />
        {fieldErrors.password && <span className="auth-register-field-error">{fieldErrors.password}</span>}
        </>}

        <button className="auth-register-primary" type="submit" disabled={loading}>
          {loading
            ? t("auth.creatingAccount")
            : verificationStep
              ? "Verify phone"
              : t("auth.createAccount")}
        </button>

        {!verificationStep && <button
          className="auth-register-secondary"
          type="button"
          onClick={() => (window.location.href = "/login")}
        >
          {t("auth.alreadyHaveAccount")}
        </button>}
      </form>

      {isDeliveryUber ? (
        <nav className="auth-register-legal-footer" aria-label="Legal">
          <button type="button" onClick={() => { window.location.href = "/delivery/courier/terms"; }}>
            Terms & Conditions
          </button>
        </nav>
      ) : null}
    </main>
  );
}

function AuthRegisterStyles() {
  return (
    <style>{`
      .auth-register-page {
        width: 100%;
        min-height: 100vh;
        min-height: 100dvh;
        height: auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 24px 16px;
        padding-bottom: 40px;
        background: linear-gradient(180deg, #0B1220 0%, #0f1d2e 100%);
        color: #fff;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Inter, sans-serif;
        overflow-x: hidden;
        overflow-y: visible;
        -webkit-overflow-scrolling: touch;
        touch-action: pan-y;
      }

      .auth-register-page * {
        box-sizing: border-box;
      }

      .auth-register-hero {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        margin-bottom: 24px;
        flex-shrink: 0;
      }

      .auth-register-hero img {
        width: 72px;
        height: 72px;
        object-fit: cover;
        border-radius: 18px;
        margin-bottom: 12px;
        box-shadow: 0 8px 32px rgba(0, 166, 81, 0.2);
      }

      .auth-register-hero span,
      .auth-register-header span {
        display: inline-flex;
        width: max-content;
        margin-bottom: 8px;
        padding: 6px 10px;
        border-radius: 999px;
        background: rgba(0, 166, 81, 0.12);
        color: #00A651;
        font-size: 11px;
        font-weight: 800;
        text-transform: uppercase;
      }

      .auth-register-hero h1 {
        margin: 0;
        font-size: 24px;
        font-weight: 800;
        line-height: 1.2;
        letter-spacing: -0.5px;
      }

      .auth-register-hero p {
        margin: 8px 0 0;
        color: rgba(255, 255, 255, 0.6);
        font-size: 14px;
        line-height: 1.4;
        max-width: 320px;
      }

      .auth-register-card {
        width: 100%;
        max-width: 420px;
        padding: 24px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 20px;
        background: rgba(255, 255, 255, 0.06);
        backdrop-filter: blur(12px);
      }

      .auth-register-header h2 {
        margin: 0 0 18px;
        font-size: 34px;
      }

      .auth-register-tabs {
        display: flex;
        gap: 6px;
        margin-bottom: 16px;
        padding: 5px;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.08);
      }

      .auth-register-tabs button,
      .auth-register-primary,
      .auth-register-secondary {
        border: 0;
        font: inherit;
        cursor: pointer;
      }

      .auth-register-tabs button {
        flex: 1;
        min-height: 42px;
        border-radius: 999px;
        background: transparent;
        color: #fff;
        font-weight: 900;
      }

      .auth-register-tabs button.active {
        background: #00A651;
        color: #fff;
      }

      .auth-register-error {
        margin-bottom: 14px;
        padding: 12px;
        border: 1px solid rgba(248, 113, 113, 0.4);
        border-radius: 14px;
        background: rgba(127, 29, 29, 0.28);
        color: #fecaca;
        font-weight: 800;
      }

      .auth-register-field-error {
        display: block;
        margin: -8px 0 10px 2px;
        padding: 0;
        color: #f87171;
        font-size: 12px;
        font-weight: 700;
        line-height: 1.3;
      }

      .auth-register-verification-copy {
        margin: 0 0 16px;
        color: rgba(255, 255, 255, 0.72);
        line-height: 1.5;
      }

      .auth-register-debug-code {
        margin-bottom: 14px;
        padding: 12px;
        border-radius: 14px;
        background: rgba(34, 197, 94, 0.14);
        color: #bbf7d0;
      }

      .auth-register-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
      }

      .auth-register-card input,
      .auth-register-card select {
        width: 100%;
        min-height: 48px;
        margin-bottom: 12px;
        padding: 0 14px;
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.08);
        color: #fff;
        font-size: 16px;
        outline: none;
        transition: border-color 150ms ease;
      }

      .auth-register-card input:focus,
      .auth-register-card select:focus {
        border-color: #00A651;
        box-shadow: 0 0 0 3px rgba(0, 166, 81, 0.15);
      }

      .auth-register-card option {
        color: #111827;
      }

      .auth-register-city {
        display: grid;
        gap: 7px;
        margin-bottom: 2px;
      }

      .auth-register-city span {
        color: rgba(255, 255, 255, 0.64);
        font-size: 12px;
      }

      .auth-register-file {
        display: grid;
        gap: 7px;
        margin-bottom: 12px;
        padding: 14px;
        border: 1px dashed rgba(0, 166, 81, 0.5);
        border-radius: 12px;
        background: rgba(0, 166, 81, 0.06);
      }

      .auth-register-file span {
        color: rgba(255, 255, 255, 0.66);
        font-size: 14px;
      }

      .auth-register-file input {
        min-height: auto;
        margin-bottom: 0;
        padding: 0;
        border: 0;
        border-radius: 0;
        background: transparent;
      }

      .auth-register-primary,
      .auth-register-secondary {
        width: 100%;
        min-height: 50px;
        border-radius: 12px;
        font-weight: 700;
        font-size: 16px;
        -webkit-tap-highlight-color: transparent;
        transition: background 150ms ease, transform 100ms ease;
      }

      .auth-register-primary {
        margin-top: 8px;
        background: #00A651;
        color: #fff;
      }

      .auth-register-primary:active {
        transform: scale(0.97);
        background: #008f45;
      }

      .auth-register-secondary {
        margin-top: 12px;
        border: 1px solid rgba(255, 255, 255, 0.15);
        background: transparent;
        color: #fff;
      }

      .auth-register-secondary:active {
        transform: scale(0.97);
        background: rgba(255, 255, 255, 0.08);
      }

      @media (max-width: 900px) {
        .auth-register-page {
          padding: 16px;
          padding-bottom: 40px;
        }

        .auth-register-hero {
          margin-bottom: 20px;
        }
      }

      @media (max-width: 560px) {
        .auth-register-page {
          padding: 12px;
          padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px));
          align-items: stretch;
        }

        .auth-register-card {
          max-width: none;
          padding: 20px;
          border-radius: 16px;
        }

        .auth-register-grid {
          grid-template-columns: 1fr;
          gap: 0;
        }

        .auth-register-hero h1 {
          font-size: 22px;
        }
      }

      /* ── Yala Delivery Uber-style registration (light courier app) ──── */

      .auth-register-page--delivery {
        min-height: 100dvh;
        align-items: center;
        padding: max(18px, env(safe-area-inset-top)) 18px calc(24px + env(safe-area-inset-bottom));
        background:
          radial-gradient(circle at 50% 0%, rgba(245, 130, 32, 0.18), transparent 34%),
          linear-gradient(180deg, #fff7ed 0%, #f8fafc 44%, #ffffff 100%) !important;
        color: #111827;
      }

      .auth-register-page--delivery .auth-register-hero {
        width: min(440px, 100%);
        margin: 8px 0 18px;
      }

      .auth-register-page--delivery .auth-register-hero img {
        width: 92px;
        height: 92px;
        border: 0;
        border-radius: 26px;
        margin-bottom: 14px;
        box-shadow: 0 18px 44px rgba(245, 130, 32, 0.16), 0 10px 30px rgba(17, 24, 39, 0.08);
      }

      .auth-register-page--delivery .auth-register-hero span,
      .auth-register-page--delivery .auth-register-header span {
        padding: 8px 14px;
        border: 1px solid rgba(245, 130, 32, 0.22);
        background: #fff3e6;
        color: #8a4b0d;
        font-size: 12px;
        letter-spacing: 0.04em;
      }

      .auth-register-page--delivery .auth-register-hero h1 {
        max-width: 360px;
        margin: 10px auto 0;
        color: #111827;
        font-size: clamp(32px, 8vw, 46px);
        font-weight: 950;
        line-height: 0.98;
        letter-spacing: -0.07em;
      }

      .auth-register-page--delivery .auth-register-hero p {
        max-width: 330px;
        margin-top: 14px;
        color: #4b5563;
        font-size: 16px;
        font-weight: 600;
        line-height: 1.35;
      }

      .auth-register-page--delivery .auth-register-card {
        width: min(430px, 100%);
        padding: 24px 22px 22px;
        border: 1px solid rgba(17, 24, 39, 0.06);
        border-radius: 30px;
        background: rgba(255, 255, 255, 0.96);
        box-shadow: 0 24px 70px rgba(17, 24, 39, 0.13);
        backdrop-filter: blur(18px);
      }

      .auth-register-page--delivery .auth-register-header h2 {
        margin-bottom: 18px;
        color: #111827;
        font-size: 38px;
        font-weight: 950;
        line-height: 1;
        letter-spacing: -0.06em;
      }

      .auth-register-page--delivery .auth-register-error {
        border-color: #fecdd3;
        background: #fff1f2;
        color: #991b1b;
        font-size: 14px;
        line-height: 1.35;
      }

      .auth-register-page--delivery .auth-register-field-error {
        color: #dc2626;
      }

      .auth-register-page--delivery .auth-register-card input,
      .auth-register-page--delivery .auth-register-card select {
        min-height: 58px;
        margin-bottom: 13px;
        padding: 0 18px;
        border: 1.5px solid #e5e7eb;
        border-radius: 18px;
        background: #f8fafc;
        color: #111827;
        font-size: 17px;
        font-weight: 750;
      }

      .auth-register-page--delivery .auth-register-card input::placeholder {
        color: #6b7280;
        opacity: 1;
      }

      .auth-register-page--delivery .auth-register-card select {
        color: #111827;
      }

      .auth-register-page--delivery .auth-register-card input:focus,
      .auth-register-page--delivery .auth-register-card select:focus {
        border-color: #f58220;
        background: #ffffff;
        box-shadow: 0 0 0 4px rgba(245, 130, 32, 0.14);
      }

      .auth-register-page--delivery .auth-register-city {
        gap: 8px;
        margin-bottom: 12px;
      }

      .auth-register-page--delivery .auth-register-city strong {
        color: #111827;
        font-size: 16px;
        font-weight: 900;
      }

      .auth-register-page--delivery .auth-register-city span {
        color: #6b7280;
        font-size: 13px;
      }

      .auth-register-page--delivery .auth-register-primary {
        min-height: 58px;
        margin-top: 12px;
        border-radius: 999px;
        background: #111827;
        color: #ffffff;
        font-size: 17px;
        font-weight: 900;
        box-shadow: 0 16px 34px rgba(245, 130, 32, 0.22);
      }

      .auth-register-page--delivery .auth-register-primary:active {
        background: #000000;
      }

      .auth-register-page--delivery .auth-register-secondary {
        min-height: 52px;
        border: 0;
        border-radius: 999px;
        background: transparent;
        color: #111827;
        font-weight: 900;
      }

      .auth-register-page--delivery .auth-register-verification-copy {
        color: #4b5563;
      }

      @media (max-width: 560px) {
        .auth-register-page--delivery {
          padding: max(14px, env(safe-area-inset-top)) 14px calc(22px + env(safe-area-inset-bottom));
          align-items: center;
        }

        .auth-register-page--delivery .auth-register-hero {
          margin-top: 2px;
          margin-bottom: 14px;
        }

        .auth-register-page--delivery .auth-register-hero img {
          width: 84px;
          height: 84px;
          border-radius: 24px;
        }

        .auth-register-page--delivery .auth-register-hero h1 {
          font-size: 34px;
        }

        .auth-register-page--delivery .auth-register-hero p {
          font-size: 15px;
        }

        .auth-register-page--delivery .auth-register-card {
          padding: 22px 18px 20px;
          border-radius: 28px;
        }

        .auth-register-page--delivery .auth-register-header h2 {
          font-size: 34px;
        }
      }
    `}</style>
  );
}

export default Register;
